import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ProblemDetailModal = ({ problem, isOpen, onClose, onApply, hasApplied = false }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !problem) return null;

  const getComplexityColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'bg-success text-success-foreground';
      case 'intermediate': return 'bg-warning text-warning-foreground';
      case 'advanced': return 'bg-error text-error-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getTechIcon = (tech) => {
    const techIcons = {
      'IoT': 'Cpu',
      'AI': 'Brain',
      'GIS': 'Map',
      'Data Science': 'BarChart3',
      'Smart City Design': 'Building2'
    };
    return techIcons?.[tech] || 'Code';
  };

  const formatDeadline = (date) => {
    const deadline = new Date(date);
    const now = new Date();
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return 'Due Tomorrow';
    return `${diffDays} days left`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' },
    { id: 'deliverables', label: 'Deliverables', icon: 'Package' },
    { id: 'contact', label: 'Contact', icon: 'User' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Building2" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{problem?.title}</h2>
              <p className="text-muted-foreground">{problem?.cityName}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-micro ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Problem Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Complexity</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(problem?.complexity)}`}>
                    {problem?.complexity}
                  </span>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Users" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Team Size</span>
                  </div>
                  <span className="font-medium text-foreground">{problem?.teamSize} members</span>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Timeline</span>
                  </div>
                  <span className="font-medium text-foreground">{problem?.timeline}</span>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Calendar" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Deadline</span>
                  </div>
                  <span className={`text-sm font-medium ${
                    formatDeadline(problem?.deadline)?.includes('Expired') ? 'text-error' :
                    formatDeadline(problem?.deadline)?.includes('Today') || formatDeadline(problem?.deadline)?.includes('Tomorrow') ? 'text-warning' :
                    'text-success'
                  }`}>
                    {formatDeadline(problem?.deadline)}
                  </span>
                </div>
              </div>

              {/* Technology Focus */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Technology Focus Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {problem?.techFocus?.map((tech, index) => (
                    <div key={index} className="flex items-center space-x-2 px-3 py-2 bg-primary/10 text-primary rounded-lg">
                      <Icon name={getTechIcon(tech)} size={16} />
                      <span className="text-sm font-medium">{tech}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Problem Description</h3>
                <p className="text-foreground leading-relaxed">{problem?.fullDescription}</p>
              </div>

              {/* City Context */}
              <div>
                <h3 className="font-medium text-foreground mb-3">City Context</h3>
                <p className="text-foreground leading-relaxed">{problem?.cityContext}</p>
              </div>
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="space-y-6">
              {/* Required Skills */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Required Skills</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {problem?.requiredSkills?.map((skill, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-accent/10 text-accent rounded-lg">
                      <Icon name="Check" size={14} />
                      <span className="text-sm">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Composition */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Preferred Team Composition</h3>
                <div className="space-y-2">
                  {problem?.teamComposition?.map((role, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="User" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">{role?.role}</p>
                        <p className="text-sm text-muted-foreground">{role?.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prerequisites */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Prerequisites</h3>
                <ul className="space-y-2">
                  {problem?.prerequisites?.map((prereq, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="CheckCircle2" size={16} className="text-success mt-0.5" />
                      <span className="text-foreground">{prereq}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'deliverables' && (
            <div className="space-y-6">
              {/* Expected Deliverables */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Expected Deliverables</h3>
                <div className="space-y-3">
                  {problem?.deliverables?.map((deliverable, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="Package" size={16} className="text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">{deliverable?.title}</p>
                        <p className="text-sm text-muted-foreground">{deliverable?.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">Due: {deliverable?.dueDate}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Success Criteria */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Success Criteria</h3>
                <ul className="space-y-2">
                  {problem?.successCriteria?.map((criteria, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Target" size={16} className="text-success mt-0.5" />
                      <span className="text-foreground">{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              {/* Primary Contact */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Primary Contact</h3>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-lg font-medium text-primary-foreground">
                        {problem?.cityContact?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{problem?.cityContact?.name}</h4>
                      <p className="text-muted-foreground">{problem?.cityContact?.title}</p>
                      <p className="text-sm text-muted-foreground">{problem?.cityContact?.department}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="Mail" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{problem?.cityContact?.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{problem?.cityContact?.phone}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Office Hours</h3>
                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                    <span className="font-medium text-foreground">Available for Questions</span>
                  </div>
                  <p className="text-foreground">{problem?.cityContact?.officeHours}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Response time: Usually within 24 hours
                  </p>
                </div>
              </div>

              {/* City Information */}
              <div>
                <h3 className="font-medium text-foreground mb-3">About {problem?.cityName}</h3>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-foreground leading-relaxed">{problem?.cityInfo}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground">
              Application deadline: {new Date(problem.deadline)?.toLocaleDateString()}
            </span>
            <span className={`text-sm font-medium ${
              formatDeadline(problem?.deadline)?.includes('Expired') ? 'text-error' :
              formatDeadline(problem?.deadline)?.includes('Today') || formatDeadline(problem?.deadline)?.includes('Tomorrow') ? 'text-warning' :
              'text-success'
            }`}>
              {formatDeadline(problem?.deadline)}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant={hasApplied ? "secondary" : "default"}
              onClick={() => onApply(problem?.id)}
              disabled={hasApplied || formatDeadline(problem?.deadline)?.includes('Expired')}
              iconName={hasApplied ? "Check" : "Send"}
              iconPosition="left"
            >
              {hasApplied ? 'Applied' : 'Apply Now'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProblemDetailModal;